import React from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  useTheme,
} from '@mui/material';
import {
  formatCurrency,
  panelSurfaceSx,
  CLIENT_EARNINGS_PAID_INFO,
} from './clientEarningsUtils';
import ClientEarningsInfoTooltip from './ClientEarningsInfoTooltip';

const PaidMetric = ({ label, value, count, info, onClick }) => {
  const clickable = typeof onClick === 'function';

  return (
    <Box sx={{ flex: 1, minWidth: 140 }}>
      <Typography
        sx={{
          fontSize: 13,
          color: 'text.secondary',
          mb: 0.5,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Typography component="span" sx={{ fontSize: 'inherit', color: 'inherit' }}>
          {label}
        </Typography>
        {info ? <ClientEarningsInfoTooltip title={info} /> : null}
      </Typography>
      <Box
        component={clickable ? 'button' : 'div'}
        type={clickable ? 'button' : undefined}
        onClick={onClick}
        sx={{
          display: 'block',
          width: '100%',
          textAlign: 'left',
          border: 'none',
          bgcolor: 'transparent',
          color: 'text.primary',
          p: 0,
          m: 0,
          cursor: clickable ? 'pointer' : 'default',
          borderRadius: 1,
          '&:hover': clickable
            ? {
                opacity: 0.85,
                '& .ce-paid-value': { textDecorationColor: 'currentColor' },
              }
            : undefined,
          '&:focus-visible': clickable
            ? {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: 2,
              }
            : undefined,
        }}
      >
        <Typography
          className="ce-paid-value"
          sx={{
            fontSize: { xs: 22, sm: 28 },
            fontWeight: 600,
            lineHeight: 1.2,
            ...(clickable
              ? {
                  textDecoration: 'underline',
                  textDecorationColor: 'transparent',
                  transition: 'text-decoration-color 0.15s',
                }
              : {}),
          }}
        >
          {value}
        </Typography>
        {typeof count === 'number' && (
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.25 }}>
            {count} job{count === 1 ? '' : 's'}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default function ClientPaidUnpaidPanel({
  data,
  loading,
  error,
  rangeLabel,
  onPaidClick,
  onUnpaidClick,
}) {
  const theme = useTheme();
  const paid = data?.invoicedPaid;

  return (
    <Box sx={panelSurfaceSx(theme)}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          px: { xs: 1.5, sm: 2.5 },
          pt: { xs: 1.5, sm: 2 },
          pb: 1,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 15,
              fontWeight: 500,
              color: 'text.secondary',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            Total Paid & Unpaid
            <ClientEarningsInfoTooltip title={CLIENT_EARNINGS_PAID_INFO.section} />
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.25 }}>
            Invoiced & Invoiced/Loss jobs only
          </Typography>
        </Box>
        {rangeLabel && (
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', flexShrink: 0 }}>
            {rangeLabel}
          </Typography>
        )}
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && error && (
        <Box sx={{ px: 2, pb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {!loading && !error && paid && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: { xs: 2, sm: 4 },
            px: { xs: 1.5, sm: 2.5 },
            pb: { xs: 1.5, sm: 2 },
          }}
        >
          <PaidMetric
            label="Total Paid"
            info={CLIENT_EARNINGS_PAID_INFO.paid}
            value={formatCurrency(paid.paidTotal)}
            count={paid.paidCount}
            onClick={onPaidClick}
          />
          <PaidMetric
            label="Total Unpaid"
            info={CLIENT_EARNINGS_PAID_INFO.unpaid}
            value={formatCurrency(paid.unpaidTotal)}
            count={paid.unpaidCount}
            onClick={onUnpaidClick}
          />
        </Box>
      )}
    </Box>
  );
}
